import { useCallback, useEffect, useMemo } from "react";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import ScriptNode from "./ScriptNode";
import { applyPositions, connect, disconnect, toGraph } from "./flowGraph";

const nodeTypes = { scriptNode: ScriptNode };

const GROUP_COLOR = {
  conversa: "#22c55e",
  logica: "#f59e0b",
  integracao: "#3b82f6",
  crm: "#a855f7",
};

const Canvas = ({ flow, catalog, selectedId, onSelect, onChange }) => {
  const graph = useMemo(() => toGraph(flow, catalog), [flow, catalog]);

  const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges);

  // O fluxo salvo é a fonte da verdade: qualquer mudança feita fora do canvas
  // (painel de configuração, paleta, importação) reconstrói o grafo.
  useEffect(() => {
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }, [graph, setNodes, setEdges]);

  const handleConnect = useCallback(
    (connection) => {
      if (!connection.source || !connection.target) return;
      // Ligar um nó a si mesmo é o laço mais fácil de criar por acidente.
      if (connection.source === connection.target) return;
      onChange(connect(flow, connection));
    },
    [flow, onChange],
  );

  const handleEdgesDelete = useCallback(
    (removed) => {
      let next = flow;
      for (const edge of removed) next = disconnect(next, edge);
      onChange(next);
    },
    [flow, onChange],
  );

  // Grava a posição só quando o arraste termina: atualizar a cada quadro
  // reescreveria o fluxo dezenas de vezes por segundo.
  const handleNodeDragStop = useCallback(
    (_event, _node, draggedNodes) => {
      const moved = draggedNodes?.length ? draggedNodes : nodes;
      onChange(applyPositions(flow, moved));
    },
    [flow, nodes, onChange],
  );

  return (
    <ReactFlow
      nodes={nodes.map((node) => ({ ...node, selected: node.id === selectedId }))}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={handleConnect}
      onEdgesDelete={handleEdgesDelete}
      onNodeDragStop={handleNodeDragStop}
      onNodeClick={(_event, node) => onSelect(node.id)}
      onPaneClick={() => onSelect(null)}
      fitView
      fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
      minZoom={0.2}
      maxZoom={1.5}
      deleteKeyCode={["Backspace", "Delete"]}
      proOptions={{ hideAttribution: false }}
    >
      <Background gap={16} size={1} />
      <Controls showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        nodeColor={(node) => GROUP_COLOR[node.data?.spec?.group] || "#9ca3af"}
        className="!bg-white dark:!bg-gray-900"
      />
    </ReactFlow>
  );
};

/** O provider precisa envolver o canvas para os hooks do React Flow valerem. */
const ScriptFlowCanvas = (props) => (
  <ReactFlowProvider>
    <Canvas {...props} />
  </ReactFlowProvider>
);

export default ScriptFlowCanvas;
